import { Dropdown as NextDropdown, Tooltip } from '@nextui-org/react';
import React, { Key, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ShimmerImage, ShimmerText } from '~/components';
import {
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemLeftContainer,
  DropDownItemLoader,
  DropDownItemText,
  Separator,
} from '~/components/Dropdown/Dropdown.styles';
import {
  NoResults,
  StyledDropdown as StyledInfiniteScrollDropdown,
  StyledDropDownMenu,
  StyledInput,
  StyledSearchIcon,
} from '~/components/InfiniteScrollDropdown/InfiniteScrollDropdown.styles';
import { CurrentOrg, useCurrentOrgContext } from '~/contexts/CurrentOrgContext';
import { getOrganizationInitials } from '~/utils/organization';

import {
  AllOrgsAvatar,
  Container,
  DropDownItemAvatar,
  TriggerAvatar,
  TriggerChevron,
  TriggerContainer,
  TriggerName,
} from './OrganizationSelector.styles';
import { useOrganizationsPagedList } from './useOrganizationsPagedList';

const KeyNoResults = 'noResults';
const KeyAllOrgs = 'all';
const scrollViewId = 'organizationSelectorScrollView';

interface OrganizationSelectorProps {
  collapsed: boolean;
}

const OrganizationSelector = ({ collapsed }: OrganizationSelectorProps) => {
  const { t } = useTranslation();
  const { activeOrg, switchOrg } = useCurrentOrgContext();

  const { items, hasMore, loadingMore, loadingFirstPage, searchTextLocal, setSearchTextLocal, handleLoadMore } =
    useOrganizationsPagedList();

  const handleSelectionChange = useCallback(
    (keys: 'all' | Set<Key>) => {
      if (keys === 'all') return;
      const [key] = Array.from(keys || []);
      if (!key || key === KeyNoResults) return;
      if (key === KeyAllOrgs) {
        switchOrg(null);
        return;
      }
      const selected = items.find((org) => org.id === key);
      if (!selected) return;
      const org: CurrentOrg = {
        id: selected.id,
        name: selected.name,
        imageUrl: selected.imageUrl,
        organizationType: selected.organizationType,
      };
      switchOrg(org);
    },
    [items, switchOrg],
  );

  const listShimmer = useMemo(
    () =>
      Array.from({ length: 5 }, (_, index) => (
        <NextDropdown.Item key={`loader-${index.toString()}`} textValue={`loader-${index.toString()}`}>
          <DropDownItemLoader>
            <ShimmerImage width="1.5rem" height="1.5rem" />
            <DropDownItemText>
              <ShimmerText width="7rem" />
            </DropDownItemText>
          </DropDownItemLoader>
        </NextDropdown.Item>
      )),
    [],
  );

  const menuComponent = useMemo(() => {
    const allOrgsItem = (
      <NextDropdown.Item key={KeyAllOrgs} textValue={t('organizations.allOrganizationsTitle')}>
        <DropDownItemContainer>
          <DropDownItemLeftContainer>
            <AllOrgsAvatar />
            <DropDownItemText>{t('organizations.allOrganizationsTitle')}</DropDownItemText>
          </DropDownItemLeftContainer>
        </DropDownItemContainer>
        <Separator />
      </NextDropdown.Item>
    );

    const orgItems = items.map((org, index) => (
      <NextDropdown.Item key={org.id} textValue={org.name}>
        <DropDownItemContainer>
          <DropDownItemLeftContainer>
            <DropDownItemAvatar src={org.imageUrl} squared text={getOrganizationInitials(org.name || '')} />
            <DropDownItemText>{org.name}</DropDownItemText>
          </DropDownItemLeftContainer>
          <DropDownItemDescription>
            {t(`organization.settings.orgTypes.${String(org.organizationType).toLowerCase()}`)}
          </DropDownItemDescription>
        </DropDownItemContainer>
        {index !== items.length - 1 && <Separator />}
      </NextDropdown.Item>
    ));

    const getTailItems = () => {
      if (loadingFirstPage || loadingMore) return listShimmer;
      if (!items.length) {
        return [
          <NextDropdown.Item key={KeyNoResults} textValue={t('common.noResults')}>
            <NoResults>{t('common.noResults')}</NoResults>
          </NextDropdown.Item>,
        ];
      }
      return [];
    };

    return (
      <StyledDropDownMenu
        width={288}
        selectionMode="single"
        disallowEmptySelection
        selectedKeys={activeOrg ? new Set([activeOrg.id]) : new Set([KeyAllOrgs])}
        onSelectionChange={handleSelectionChange}
        id={scrollViewId}
      >
        {[allOrgsItem, ...orgItems, ...getTailItems()]}
      </StyledDropDownMenu>
    );
  }, [activeOrg, handleSelectionChange, items, listShimmer, loadingFirstPage, loadingMore, t]);

  const triggerName = activeOrg?.name || t('organizations.allOrganizationsTitle');

  const trigger = (
    <NextDropdown.Trigger>
      <TriggerContainer type="button" $collapsed={collapsed}>
        {activeOrg ? (
          <TriggerAvatar src={activeOrg.imageUrl} squared text={getOrganizationInitials(activeOrg.name || '')} />
        ) : (
          <AllOrgsAvatar />
        )}
        {!collapsed && (
          <>
            <TriggerName>{triggerName}</TriggerName>
            <TriggerChevron />
          </>
        )}
      </TriggerContainer>
    </NextDropdown.Trigger>
  );

  return (
    <Container>
      <StyledInfiniteScrollDropdown isBordered>
        {collapsed ? (
          <Tooltip content={triggerName} placement="right" hideArrow portalClassName="tooltip-wrapper">
            {trigger}
          </Tooltip>
        ) : (
          trigger
        )}
        <>
          <StyledInput
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            iconLeft={<StyledSearchIcon />}
            clearable
            onChange={(e: any) => setSearchTextLocal(e.target.value as string)}
            value={searchTextLocal}
            removeMargin
            containerMarginTop="0.75rem"
            marginBottom="0.25rem"
          />
          <InfiniteScroll
            dataLength={items.length}
            next={() => handleLoadMore()}
            hasMore={hasMore}
            hasChildren
            scrollThreshold="20px"
            scrollableTarget={scrollViewId}
            loader={null}
          >
            {menuComponent}
          </InfiniteScroll>
        </>
      </StyledInfiniteScrollDropdown>
    </Container>
  );
};

export default OrganizationSelector;
